import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Home as HomeIcon, Building2, Factory, Landmark, Route as RouteIcon, Hammer, Sofa, PenTool, DraftingCompass, Calculator, ClipboardList, Truck, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { Overline, FadeIn } from "@/components/site/Shared";

export const SERVICE_ICONS = {
  Home: HomeIcon, Building2, Factory, Landmark, Route: RouteIcon, Hammer, Sofa,
  PenTool, DraftingCompass, Calculator, ClipboardList, Truck, KeyRound,
};

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="services-page">
      <section className="py-24 sm:py-32 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">Our Services</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95] max-w-4xl">
              Thirteen Disciplines.<br /><span className="text-ember">One Standard.</span>
            </h1>
            <p className="text-silver max-w-2xl mt-8 leading-relaxed">
              From a single villa to an eleven-kilometre corridor, every OGK service runs on the same operating system: measurable scopes, milestone billing, daily reporting and engineering-grade quality control.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[s.icon] || Building2;
              return (
                <FadeIn key={s.slug} delay={(i % 3) * 0.06}>
                  <Link to={`/services/${s.slug}`} data-testid={`service-list-card-${s.slug}`}
                    className="group bg-carbon p-9 h-full flex flex-col hover:bg-graphite transition-colors duration-300">
                    <div className="flex items-start justify-between">
                      <Icon size={28} className="text-ember" strokeWidth={1.5} />
                      <span className="font-heading text-silver/40 text-sm">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h3 className="font-heading text-xl text-white uppercase tracking-tight mt-7 leading-tight">{s.title}</h3>
                    <p className="text-ember text-xs uppercase tracking-[0.15em] font-bold mt-2">{s.tagline}</p>
                    <p className="text-silver text-sm mt-4 leading-relaxed flex-1 line-clamp-3">{s.description}</p>
                    <span className="mt-7 text-xs uppercase tracking-[0.2em] text-silver group-hover:text-ember flex items-center gap-2 transition-colors duration-200">
                      Details & FAQs <ArrowUpRight size={13} />
                    </span>
                  </Link>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-ember">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tighter text-white">Not Sure Which Service Fits?</h2>
          <Link to="/book-site-visit" data-testid="services-cta-btn" className="bg-carbon text-white uppercase text-sm tracking-[0.15em] font-bold px-8 py-4 flex items-center gap-2 hover:bg-black transition-colors duration-200">
            Book A Free Site Visit <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
